import { useEffect, useRef } from 'react'
import type { KeyboardEvent } from 'react'

/**
 * One-box-per-digit verification code — the same surface as the consumer
 * checkout (parkify-pay/consumer/src/ui.tsx), so the portal sign-in matches the
 * normal customer journey instead of a single text field. Ported app-local
 * (each app owns its small UI primitives; styles live in index.css `.pk-otp*`,
 * which resolve against shared @parkify/brand-tokens vars).
 *
 * Handles manual typing (auto-advance + backspace), paste, and the SMS-autofill
 * case where the OS drops the whole code into one box — all funnel through
 * `commit`, the single source of truth for the combined value.
 */
type OtpInputProps = {
  /** The code entered so far (digits only, left-aligned). */
  value: string
  onChange: (code: string) => void
  /** Number of boxes (Twilio Verify codes are 6 digits). */
  length?: number
  disabled?: boolean
  /** Paint every box in the error state (red border). */
  error?: boolean
  /** Focus the first box on mount. */
  autoFocus?: boolean
  /** Set on the first box so an external `<label htmlFor>` can target the group. */
  id?: string
  /** Fires once all boxes are filled — used to auto-submit, like Stripe/Link. */
  onComplete?: (code: string) => void
  'aria-label'?: string
}

export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
  error,
  autoFocus,
  id,
  onComplete,
  'aria-label': ariaLabel = 'Verification code',
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = value.replace(/\D/g, '').slice(0, length)

  // Auto-submit disables the boxes mid-keystroke, so a rejected code drops focus
  // to <body>. When the error surfaces, pull focus back to the first box so a
  // retry is one keystroke away (the boxes re-enable as `disabled` clears).
  useEffect(() => {
    if (error) refs.current[0]?.focus()
  }, [error])

  function focusBox(i: number) {
    const n = Math.max(0, Math.min(i, length - 1))
    const el = refs.current[n]
    el?.focus()
    el?.select()
  }

  function commit(next: string) {
    const clean = next.replace(/\D/g, '').slice(0, length)
    onChange(clean)
    if (clean.length >= length) onComplete?.(clean)
  }

  function handleChange(i: number, raw: string) {
    const incoming = raw.replace(/\D/g, '')
    if (incoming.length > 1) {
      // Paste or SMS autofill dropped the whole code into one box → spread it
      // across all boxes from the start.
      commit(incoming)
      focusBox(incoming.length)
      return
    }
    if (incoming.length === 0) {
      // Box cleared via delete/cut.
      commit(digits.slice(0, i) + digits.slice(i + 1))
      return
    }
    // Single digit. Clamp the insertion point to the current length so a
    // click-ahead into an empty box never leaves a gap in the middle.
    const at = Math.min(i, digits.length)
    commit(digits.slice(0, at) + incoming + digits.slice(at + 1))
    focusBox(at + 1)
  }

  function handleKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      // Empty box → backspace deletes the previous digit and steps back.
      e.preventDefault()
      commit(digits.slice(0, i - 1) + digits.slice(i))
      focusBox(i - 1)
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault()
      focusBox(i - 1)
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      e.preventDefault()
      focusBox(i + 1)
    }
  }

  return (
    <div className={`pk-otp${error ? ' pk-otp--error' : ''}`} role="group" aria-label={ariaLabel}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          className="pk-otp__box"
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          id={i === 0 ? id : undefined}
          name={i === 0 ? 'otp' : undefined}
          aria-label={`Digit ${i + 1}`}
          // No `maxLength={1}`: iOS SMS autofill (and desktop paste) drops the
          // whole code into the first box, and a hard cap of 1 makes the browser
          // truncate it to a single digit *before* `onChange` fires — so only
          // the first digit ever lands ("stuck on one"). We let the full string
          // reach `handleChange`, which spreads it across the boxes; `commit`
          // caps the total length and `value` keeps each box to one digit.
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          value={digits[i] ?? ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.currentTarget.select()}
        />
      ))}
    </div>
  )
}
